// Ring 1 — shared card-glyph primitive used at TWO scales:
//   - 24×36 viewport (CashierScene + future RunOverScene preview)
//   - 120×180 share-card composition (ShareCardSystem)
//
// Both consumers call `makeCardGlyph(scene, x, y, card, scale)`. SUIT_COLOR
// map is extracted from Phase-1 CashierScene (CONTEXT.md <code_context>
// "Reusable Assets") so there's one source of truth for ♥♦/♠♣ palette.
//
// Owns CARD_GLYPH_TEXT_PX as a card-component-internal constant per UI-SPEC
// §Typography "Card-component-internal constants" — NOT a layout type-scale
// decision; the size is dictated by the card-frame footprint and goes away
// entirely when Phase 3 (VISUAL-03) swaps the placeholder for real sprites.
//
// CLAUDE.md "no magic numbers in entity files" exemption: visual constants
// co-located with the visual primitive (same exemption as PocketStrip /
// CashierScene placeholder block).

import Phaser from 'phaser';
import type { Card } from '../run/types';

/** Card-glyph palette — INHERITED from Phase-1 CashierScene Phase-1 D-14.
 *  Carries semantic data (suit identity), NOT design-system accent. UI-SPEC §Color confirms. */
export const SUIT_COLOR: Record<Card['suit'], number> = {
  h: 0xff5566,
  d: 0xff5566,
  c: 0xf5e1c8,
  s: 0xf5e1c8,
};

const STROKE_COLOR = 0x08050a;
const TEXT_COLOR = '#08050a';

export interface CardGlyphScale {
  /** Card frame width in px. */
  cardW: number;
  /** Card frame height in px. */
  cardH: number;
  /** Inner rank+suit text size (CARD_GLYPH_TEXT_PX) in px. */
  textPx: number;
  /** Stroke width in px. */
  strokePx: number;
}

/** Phase-1 viewport scale — 24×36 cards, 10px text, 1px stroke. */
export const CARD_GLYPH_VIEWPORT: CardGlyphScale = {
  cardW: 24,
  cardH: 36,
  textPx: 10,
  strokePx: 1,
};

/** Phase-2 share-card scale — 120×180 cards, 64px text, 4px stroke. */
export const CARD_GLYPH_SHARE: CardGlyphScale = {
  cardW: 120,
  cardH: 180,
  textPx: 64,
  strokePx: 4,
};

export interface CardGlyphResult {
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
}

/**
 * Render one card glyph (rectangle + unicode rank+suit text) at the requested
 * scale. Returns the underlying GameObjects so the caller can pass them to
 * RenderTexture.draw() (off-screen composition) or hand-add them to the
 * scene's display list (CashierScene's standard `scene.add.*` path).
 *
 * IMPORTANT: this factory uses `scene.add.*`. For off-screen composition
 * (ShareCardSystem), the caller should use `scene.make.*` directly with
 * `addToScene = false` to bypass the display list — `makeCardGlyph` itself is
 * NOT used at the share-card scale to avoid one-frame flicker risk on slow
 * GPUs (display-list addition followed by setVisible(false) can render once
 * before the toggle takes effect).
 *
 * Glyph text format: `${rank}${suit-letter}` matches Phase-1 CashierScene
 * exactly (lowercase suit letter, e.g. "As", "Kh"). Phase 3 may swap the
 * letter form for unicode glyph form (e.g. "A♠") if real card sprites land
 * — for now, preserve Phase-1 text content for byte-identical output.
 */
export function makeCardGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  card: Card,
  scale: CardGlyphScale,
): CardGlyphResult {
  const bg = scene.add.rectangle(x, y, scale.cardW, scale.cardH, SUIT_COLOR[card.suit], 1);
  bg.setStrokeStyle(scale.strokePx, STROKE_COLOR);
  const label = scene.add.text(x, y, `${card.rank}${card.suit}`, {
    fontFamily: 'monospace',
    fontSize: `${scale.textPx}px`,
    color: TEXT_COLOR,
  }).setOrigin(0.5);
  return { bg, label };
}
